// ---------------------------------------------------------------------------
// The Stock tab's checkout (contract v14): a task adds materials to its list
// without ordering each on the spot, then reviews them once. The review sorts
// every material by what it still needs:
//
//   - enough stock on site, or this task's share of an order already covers
//     it → nothing to do, not in the plan;
//   - an order for the material already on its way → the amount is ADDED to
//     it (this task's own order for the difference, another task's for the
//     whole need) — `joins`;
//   - an order on its way that cannot take it (another unit, no unit, or a
//     typed name matched by spelling alone) → `blocked`: a new line for the
//     material would hit the one-open-line rule, so it needs a change first;
//   - everything else → one NEW multi-material order — `newLines`.
//
// No React, no query: `useTaskStock` supplies the two lookups.
// ---------------------------------------------------------------------------

import type { Delivery, InventoryLevel } from '@interfaces/ILogistics';
import type { ITaskStockClaim } from '@interfaces/IScheduler';
import { needOf, round1, type IClaimEvaluation } from './claimEvaluation';
import { bookingCovers, joinableOrder, type IJoinableOrder, type IOwnBooking, type JoinRefusal } from './joinOrder';

/** A material the checkout adds to an order already on its way, and how
 *  much: the whole need, or what this task's share falls short of it by. */
export interface IPlannedJoin {
  claim: ITaskStockClaim;
  order: IJoinableOrder;
  amount: number;
}

/** A material whose open order cannot take it — and why. */
export interface IBlockedItem {
  claim: ITaskStockClaim;
  order: Delivery;
  reason: JoinRefusal;
}

/** A material the new order carries: the need it is raised for, and the
 *  site's running stock of it when there is some (a partial read), which the
 *  order's prefill reads a lead time and daily rate from. */
export interface INewLine {
  claim: ITaskStockClaim;
  need: number;
  level: InventoryLevel | null;
}

export interface ICheckoutPlan {
  joins: IPlannedJoin[];
  blocked: IBlockedItem[];
  newLines: INewLine[];
  /** Every material the plan names — what the checkout bar counts. */
  count: number;
}

/** The two answers the plan reads per material (`useTaskStock` has both). */
export interface ICheckoutContext {
  bookingFor(materialName: string): IOwnBooking | null;
  openOrderFor(materialName: string): Delivery | null;
}

/** `claims[i]` is read against `evaluations[i]`, in list order. */
export function checkoutPlan(
  claims: ITaskStockClaim[],
  evaluations: IClaimEvaluation[],
  ctx: ICheckoutContext,
): ICheckoutPlan {
  const joins: IPlannedJoin[] = [];
  const blocked: IBlockedItem[] = [];
  const newLines: INewLine[] = [];
  claims.forEach((claim, i) => {
    const { outcome } = evaluations[i]!;
    // Covered, blank, over the ceiling, or a unit the stock disagrees with:
    // none of those is answered by an order.
    const need = needOf(outcome);
    if (need <= 0) return;
    const booking = ctx.bookingFor(claim.materialName);
    if (bookingCovers(booking, need)) return;
    // This task's own order first — it is the one the task is already on —
    // then whichever order is on its way for the material.
    const target = booking?.order ?? ctx.openOrderFor(claim.materialName);
    if (target === null) {
      newLines.push({ claim, need, level: outcome.kind === 'partial' ? outcome.level : null });
      return;
    }
    const joinable = joinableOrder(target, claim, booking !== null);
    if (typeof joinable === 'string') {
      blocked.push({ claim, order: target, reason: joinable });
      return;
    }
    joins.push({ claim, order: joinable, amount: booking === null ? need : round1(need - booking.share) });
  });
  return { joins, blocked, newLines, count: joins.length + blocked.length + newLines.length };
}

/**
 * Whether the checkout still has something to do for this claim — an add, a
 * line on a new order, or a refusal to resolve.
 *
 * The Stock tab's page lists what the task has SECURED (ruling 2026-09-16):
 * stock on site, or an order that carries it. Everything else waits behind
 * "Review order", which is what that bar is for — so the page never fills up
 * with materials nobody has ordered yet.
 */
export function planHas(plan: ICheckoutPlan, claimId: string): boolean {
  return (
    plan.joins.some((join) => join.claim.id === claimId) ||
    plan.blocked.some((item) => item.claim.id === claimId) ||
    plan.newLines.some((line) => line.claim.id === claimId)
  );
}
